import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Menu } from 'lucide-react';
import Input from '@ajat/design-system/Input';

/** Every brand the dashboard can preview — the single list (Sidebar reads it too). */
export const BRANDS = ['CLS', 'ABC', 'Overall'];

/** ⌘K on a Mac, Ctrl-K elsewhere. */
const isMac =
  typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform || '');

/**
 * Topbar — search + brand switch.
 *
 * Search reuses the DS `Input type="searchable"`, which already ships the
 * magnifier, the match highlighting and the dropdown panel; the registry's
 * flat page list is fed straight in as `options`.
 *
 * It matches on label only — that is the DS Input's own filter
 * (`(opt.label || opt.value).includes(...)`). Matching group names too would
 * mean fighting that filter or replacing the component, and with 47
 * distinctly-named entries the collapsible rail covers browsing instead.
 */
const Topbar = ({ searchOptions, onSelectPage, brand, onBrandChange, onOpenMenu }) => {
  // Clear the field after navigating so the next search starts empty.
  const [searchKey, setSearchKey] = useState(0);
  const searchRef = useRef(null);

  // ⌘K / Ctrl-K focuses search from anywhere; Escape gives the page back.
  useEffect(() => {
    const onKeyDown = (event) => {
      const wantsSearch =
        (isMac ? event.metaKey : event.ctrlKey) && event.key?.toLowerCase() === 'k';
      if (wantsSearch) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (event.key === 'Escape' && document.activeElement === searchRef.current) {
        searchRef.current.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSelect = (option) => {
    if (!option) return;
    onSelectPage(option.value);
    setSearchKey((k) => k + 1);
  };

  return (
    <div className="ds-page__topbar">
      <button
        type="button"
        onClick={onOpenMenu}
        className="ds-page__menu-btn"
        aria-label="Open navigation"
      >
        <Menu size={18} />
      </button>

      <div className="ds-page__search">
        <Input
          key={searchKey}
          ref={searchRef}
          type="searchable"
          label={`Search components  ${isMac ? '⌘K' : 'Ctrl K'}`}
          options={searchOptions}
          onSelect={handleSelect}
          showOptionIcons={false}
          showClear
        />
      </div>

      <div className="ds-page__topbar-end">
        <div className="ds-page__brand-switch" role="group" aria-label="Brand">
          {BRANDS.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => onBrandChange(b)}
              aria-pressed={brand === b}
              className={`typo-btn-tertiary ds-page__brand-option${
                brand === b ? ' ds-page__brand-option--active' : ''
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

Topbar.propTypes = {
  /** [{ value: slug, label }] built from the registry */
  searchOptions: PropTypes.array.isRequired,
  /** Called with a page slug when a search result is picked */
  onSelectPage: PropTypes.func.isRequired,
  /** Active brand — 'CLS' | 'ABC' | 'Overall' */
  brand: PropTypes.oneOf(BRANDS).isRequired,
  /** Switches brand (and the `data-brand` attribute on <html>) */
  onBrandChange: PropTypes.func.isRequired,
  /** Opens the mobile nav drawer */
  onOpenMenu: PropTypes.func.isRequired,
};

export default Topbar;
